import type { Permission } from "@/types";
import type {
  StaffAccessProfile,
  StaffRolePreset,
  StaffRolePresetRead,
} from "@/services/staffAccessService";
import { ASSIGNABLE_STAFF_PERMISSIONS } from "./staffPermissionScopes";

export type StaffAccessDraft =
  | { mode: "preset"; key: StaffRolePreset }
  | { mode: "custom"; permissions: Set<Permission> };

export function assignablePermissionSet(
  source: Iterable<string>,
): Set<Permission> {
  const assignable = new Set<string>(ASSIGNABLE_STAFF_PERMISSIONS);
  return new Set(
    [...source].filter((permission): permission is Permission =>
      assignable.has(permission),
    ),
  );
}

function samePermissionSet(
  left: ReadonlySet<Permission>,
  right: readonly Permission[],
): boolean {
  if (left.size !== right.length) return false;
  return right.every((permission) => left.has(permission));
}

export function draftFromProfile(
  profile: StaffAccessProfile,
): StaffAccessDraft {
  if (profile.preset_key) {
    return { mode: "preset", key: profile.preset_key };
  }
  return { mode: "custom", permissions: new Set(profile.permissions) };
}

export function isDraftDirty(
  draft: StaffAccessDraft,
  profile: StaffAccessProfile,
): boolean {
  if (draft.mode === "preset") {
    return draft.key !== profile.preset_key;
  }
  if (profile.preset_key !== null) return true;
  return !samePermissionSet(draft.permissions, profile.permissions);
}

export function displayedPermissions(
  draft: StaffAccessDraft,
  presets: StaffRolePresetRead[],
  profile: StaffAccessProfile,
): ReadonlySet<Permission> {
  if (draft.mode === "custom") return draft.permissions;
  const preset = presets.find((item) => item.key === draft.key);
  return new Set(preset?.permissions ?? profile.permissions);
}
